import { sendEmailVerification } from "firebase/auth";
import { captureException } from "@sentry/react";

const COOLDOWN_MS = 60_000;
const deadlines = new Map();
const pending = new Map();
const messages = new Map();
const keyFor = (user) => `verification-email:${user.auth?.app?.options?.projectId || "default"}:${user.uid}`;

export const verificationMessage = (user) => user ? messages.get(keyFor(user)) || "" : "";

export function verificationSecondsRemaining(user) {
  if (!user) return 0;
  const key = keyFor(user);
  let until = deadlines.get(key) || 0;
  try {
    until = Math.max(until, Number(localStorage.getItem(key)) || 0);
  } catch (_) {
    // In-memory protection still works when browser storage is unavailable.
  }
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

function startCooldown(user) {
  const key = keyFor(user);
  const until = Date.now() + COOLDOWN_MS;
  deadlines.set(key, until);
  try {
    localStorage.setItem(key, String(until));
  } catch (_) {}
}

// Signup and resend share both the in-flight request and a per-account cooldown.
export function requestVerificationEmail(user, source = "resend") {
  if (!user) return Promise.resolve({ status: "error", message: "Please sign in again to verify your email." });
  if (user.emailVerified) return Promise.resolve({ status: "verified", message: "Your email is already verified. Refresh the page to continue." });
  const key = keyFor(user);
  if (pending.has(key)) return pending.get(key);
  if (verificationSecondsRemaining(user) > 0) {
    return Promise.resolve({ status: "cooldown", message: "Please wait before requesting another email. Check your inbox and spam folder." });
  }
  startCooldown(user);
  const request = Promise.resolve().then(async () => {
    try {
      await sendEmailVerification(user);
      startCooldown(user);
      return { status: "sent", message: "Verification email sent. Check your inbox and spam folder." };
    } catch (error) {
      startCooldown(user);
      captureException(error, {
        level: error.code === "auth/too-many-requests" ? "warning" : "error",
        tags: { operation: "email_verification", source },
      });
      return {
        status: "error",
        message: error.code === "auth/too-many-requests"
          ? "Too many attempts. Check your inbox and spam folder, or try again later."
          : "We couldn't send the verification email. Please try again shortly.",
      };
    } finally {
      pending.delete(key);
    }
  }).then((result) => {
    messages.set(key, result.message);
    return result;
  });
  pending.set(key, request);
  return request;
}
